// src/components/ui/AhaUpsellModal.jsx
import React, { useMemo, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, X, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate';
import { trackPaywallShown } from '../../utils/conversionEvents';
import { useRegionalPricing } from '../../hooks/useRegionalPricing';

/**
 * AhaUpsellModal
 *
 * @param {boolean} open
 * @param {function} onDismiss - Called when user dismisses the modal
 * @param {function} onUpgrade - Called before routing to checkout
 * @param {'exam'|'lesson'} context
 */
export default function AhaUpsellModal({ open, onDismiss, onUpgrade, context = 'exam' }) {
  const { t } = useTranslation();
  const navigate = useLocalizedNavigate();
  // Default to the "Most Popular" plan so the highlighted (black) card matches
  // the popular badge; clicking another card moves the highlight with the selection.
  const [selectedPlan, setSelectedPlan] = useState('quarterly');
  const { getPriceLabel: getPlanPriceLabel } = useRegionalPricing();

  useEffect(() => {
    if (open) trackPaywallShown({ context });
  }, [open, context]);

  const plans = useMemo(() => {
    const quarterlyFeatures = t('aha.lineup.quarterly.features', { returnObjects: true });
    const premiumFeatures = t('aha.lineup.premium.features', { returnObjects: true });

    return [
      {
        id: 'quarterly',
        price: getPlanPriceLabel('quarterly'),
        isPopular: true,
        features: Array.isArray(quarterlyFeatures) ? quarterlyFeatures : [],
      },
      {
        id: 'premium',
        price: getPlanPriceLabel('premium'),
        features: Array.isArray(premiumFeatures) ? premiumFeatures : [],
      },
    ];
  }, [t, getPlanPriceLabel]);

  const handleUpgrade = () => {
    onUpgrade?.();
    navigate(`/checkout?planId=${selectedPlan}`);
  };

  const handleDismiss = () => {
    onDismiss?.();
  };

  const contextHeadlines = {
    lesson: t('aha.lesson.headline'),
    lesen: t('aha.lesen.headline'),
    schreiben: t('aha.schreiben.headline'),
    horen: t('aha.horen.headline'),
    vocabulary: t('aha.vocabulary.headline'),
    redemittel: t('aha.redemittel.headline'),
  };
  const contextSublines = {
    lesson: t('aha.lesson.subline'),
    lesen: t('aha.lesen.subline'),
    schreiben: t('aha.schreiben.subline'),
    horen: t('aha.horen.subline'),
    vocabulary: t('aha.vocabulary.subline'),
    redemittel: t('aha.redemittel.subline'),
  };
  const headline = contextHeadlines[context] ?? t('aha.exam.headline');
  const subline = contextSublines[context] ?? t('aha.exam.subline');

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="aha-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6"
          onClick={handleDismiss}
        >
          <motion.div
            key="aha-modal"
            initial={{ y: 28, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 28, opacity: 0, scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="w-full max-w-3xl rounded-3xl bg-white p-5 sm:p-8 shadow-2xl max-h-[92dvh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-6 sm:mb-7">
              <div className="flex items-start gap-3.5 sm:gap-4">
                <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-black flex-shrink-0 mt-0.5">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="font-space text-xl sm:text-2xl font-black text-black tracking-tight leading-tight">
                    {headline}
                  </h2>
                  <p className="text-sm sm:text-base text-black/60 leading-snug mt-1.5 sm:max-w-2xl">
                    {subline}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={handleDismiss}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0 ml-2 touch-manipulation"
                aria-label={t('aha.close')}
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            <div className={`grid grid-cols-1 ${plans.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-4 sm:gap-5 mb-6 sm:mb-7`}>
              {plans.map((plan) => {
                const isSelected = selectedPlan === plan.id;

                return (
                  <button
                    key={plan.id}
                    type="button"
                    onClick={() => setSelectedPlan(plan.id)}
                    className={`relative flex flex-col justify-start text-left rounded-3xl border-2 p-4 sm:p-5 touch-manipulation transition-all duration-200 min-h-[220px] ${
                      isSelected
                        ? 'bg-black border-black text-white shadow-lg shadow-black/15'
                        : 'bg-white border-slate-200 text-black hover:border-black/20'
                    }`}
                  >
                    {plan.isPopular && (
                      <span className="absolute top-3 right-3 text-[10px] font-bold tracking-wide uppercase px-2 py-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 whitespace-nowrap">
                        {t('pricing.plans.popular', 'Most Popular')}
                      </span>
                    )}

                    <p className="font-space text-sm sm:text-base font-black mb-2 pr-24">
                      {t(`pricing.lineup.${plan.id}.name`)}
                    </p>

                    <div className="mb-1.5 flex items-baseline gap-1.5">
                      <span className="font-space text-4xl sm:text-[2.6rem] font-black leading-none">{plan.price}</span>
                    </div>

                    <p
                      className={`text-xs uppercase tracking-wider mb-3.5 ${
                        isSelected ? 'text-white/60' : 'text-black/45'
                      }`}
                    >
                      {t(`aha.lineup.${plan.id}.cycle`)}
                    </p>

                    <ul className="space-y-2">
                      {plan.features.map((feature, idx) => (
                        <li key={`${plan.id}-feature-${idx}`} className="flex items-start gap-1.5">
                          <CheckCircle2
                            className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${
                              isSelected ? 'text-white' : 'text-black'
                            }`}
                          />
                          <span className={`text-sm leading-snug ${isSelected ? 'text-white/80' : 'text-black/65'}`}>
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              onClick={handleUpgrade}
              className="w-full min-h-[56px] flex items-center justify-center rounded-2xl bg-black text-white font-bold text-base hover:bg-gray-900 active:scale-[0.98] transition-all touch-manipulation mb-2.5"
            >
              {t('aha.cta.upgrade')} - {plans.find((p) => p.id === selectedPlan)?.price}
            </button>

            <button
              type="button"
              onClick={handleDismiss}
              className="w-full min-h-[46px] flex items-center justify-center rounded-2xl text-black/45 text-base hover:text-black/70 transition-colors touch-manipulation"
            >
              {t('aha.cta.later')}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
